import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { ICONS } from '../constants';
import { API_CONFIG } from '../config';
import { getCurrencySymbol } from '../utils/currency';

interface Shot {
  id: string;
  shotNumber: number;
  thumbnailUrl?: string;
}

interface Assignment {
  id?: string;
  shotId: string;
  artistId?: string | null; // Optional - can be null for virtual artists or unassigned
  specialization: string;
  estimatedHours: number;
  hourlyRate: number;
  fixedAmount?: number | null;
  isVirtualArtist?: boolean;
  virtualArtistType?: 'junior' | 'mid' | 'senior' | 'lead' | null;
}

interface ParticipantDetail {
  shot: Shot;
  assignment: Assignment;
  payment: number;
}

interface ParticipantDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  participantName: string;
  participantColor: string;
  participantId: string;
  biddingSheetId: string;
  details: ParticipantDetail[];
  currency: string;
}

const ParticipantDetailsModal: React.FC<ParticipantDetailsModalProps> = ({
  isOpen,
  onClose,
  participantName,
  participantColor,
  participantId,
  biddingSheetId,
  details,
  currency
}) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  if (!isOpen) return null;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(
        `${API_CONFIG.API_URL}/export/bidding/${biddingSheetId}/csv?artistId=${participantId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Try to get filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `${participantName}_${new Date().toISOString().split('T')[0]}.csv`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }
        a.download = filename;
        
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Export failed:', response.statusText);
        alert('Failed to export data');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const totalPayment = details.reduce((sum, detail) => sum + detail.payment, 0);
  const totalHours = details.reduce((sum, detail) => sum + Number(detail.assignment.estimatedHours || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100]" onClick={onClose}>
      <div 
        className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className={`w-4 h-4 rounded-full ${participantColor}`} />
            <h2 className="text-xl font-bold text-white">{participantName}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ICONS.X className="w-6 h-6" />
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-900/50 rounded-lg">
          <div className="text-center">
            <div className="text-gray-400 text-sm mb-1">{t('bidding.shotsUnit') || 'Shots'}</div>
            <div className="text-white text-xl font-bold">{details.length}</div>
          </div>
          <div className="text-center border-x border-gray-700">
            <div className="text-gray-400 text-sm mb-1">{t('bidding.totalHours') || 'Total Hours'}</div>
            <div className="text-white text-xl font-bold">{totalHours.toFixed(1)}h</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 text-sm mb-1">{t('bidding.totalPayment') || 'Total Payment'}</div>
            <div className="text-white text-xl font-bold">{getCurrencySymbol(currency)}{totalPayment.toFixed(2)}</div>
          </div>
        </div>

        {/* Details Table */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-2">
            {details.map((detail, index) => (
              <div
                key={index}
                className="bg-gray-900/30 rounded-lg p-3 hover:bg-gray-900/50 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  {/* Thumbnail */}
                  <div className="flex-shrink-0">
                    {detail.shot.thumbnailUrl ? (
                      <img
                        src={detail.shot.thumbnailUrl}
                        alt={`Shot ${detail.shot.shotNumber}`}
                        className="w-20 h-12 object-cover rounded border border-gray-700"
                      />
                    ) : (
                      <div className="w-20 h-12 bg-gray-800 rounded border border-gray-700 flex items-center justify-center">
                        <ICONS.Image className="w-6 h-6 text-gray-600" />
                      </div>
                    )}
                  </div>

                  {/* Shot Info */}
                  <div className="flex-1 grid grid-cols-5 gap-4 items-center">
                    <div>
                      <div className="text-xs text-gray-500">{t('bidding.shotNumber') || 'Shot'}</div>
                      <div className="text-white font-semibold">#{detail.shot.shotNumber}</div>
                    </div>
                    
                    <div>
                      <div className="text-xs text-gray-500">{t('bidding.specialization') || 'Role'}</div>
                      <div className="text-gray-300">{detail.assignment.specialization}</div>
                    </div>
                    
                    <div>
                      <div className="text-xs text-gray-500">{t('bidding.hours') || 'Hours'}</div>
                      <div className="text-gray-300">{Number(detail.assignment.estimatedHours || 0).toFixed(1)}h</div>
                    </div>
                    
                    <div>
                      <div className="text-xs text-gray-500">{t('bidding.rate') || 'Rate'}</div>
                      <div className="text-gray-300">{getCurrencySymbol(currency)}{Number(detail.assignment.hourlyRate || 0).toFixed(2)}/h</div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-xs text-gray-500">{t('bidding.payment') || 'Payment'}</div>
                      <div className="text-white font-semibold">{getCurrencySymbol(currency)}{detail.payment.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-700 flex justify-between">
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isExporting ? (
              <>
                <ICONS.Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('common.processing')}</span>
              </>
            ) : (
              <>
                <ICONS.Download className="w-4 h-4" />
                <span>{t('bidding.exportParticipant')}</span>
              </>
            )}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParticipantDetailsModal;

